import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Button,
  styled,
  SwipeableDrawer,
  IconButton,
  Slider,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import useCategories from "../../../hooks/api-request/useCategories";

const FilterHeader = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 600,
  marginBottom: "16px",
}));

const FilterAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  "&:before": {
    display: "none",
  },
  "& .MuiAccordionSummary-root": {
    padding: 0,
    minHeight: "48px",
  },
  "& .MuiAccordionDetails-root": {
    padding: "0 0 16px 0",
  },
}));

const MobileFilterWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingTop: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

const MobileHeader = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  zIndex: 1,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const PriceInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const PriceInput = styled(TextField)(({ theme }) => ({
  "& input": {
    padding: "8px 14px",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
  },
}));

const PriceSlider = styled(Slider)(({ theme }) => ({
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    "&:focus, &:hover, &.Mui-active": {
      boxShadow: "inherit",
    },
  },
  "& .MuiSlider-track": {
    height: 4,
  },
  "& .MuiSlider-rail": {
    height: 4,
    opacity: 0.2,
  },
}));

const FORMAT_OPTIONS = [
  { value: "PDF", label: "eBook" },
  { value: "Audiobook", label: "Audiobook" },
];

const FilterContent = ({ filters, onFilterChange, categories }) => {
  const { t } = useTranslation();
  const [priceRange, setPriceRange] = useState([
    filters.minPrice || 0,
    filters.maxPrice || 200,
  ]);

  const handleFormatChange = (format) => {
    onFilterChange({
      ...filters,
      format: filters.format === format ? null : format,
    });
  };

  const handleCategoryChange = (categoryId) => {
    onFilterChange({
      ...filters,
      category: filters.category === categoryId ? null : categoryId,
    });
  };

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const handlePriceInputChange = (type) => (event) => {
    const value = event.target.value === "" ? "" : Number(event.target.value);
    const newRange = [...priceRange];

    if (type === "min") {
      newRange[0] = value;
    } else {
      newRange[1] = value;
    }

    setPriceRange(newRange);
  };

  const handlePriceCommitted = () => {
    onFilterChange({
      ...filters,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
    });
  };

  return (
    <>
      <FilterAccordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{t("audioBooks.filter.categories")}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {categories?.map((category) => (
              <FormControlLabel
                key={category.id}
                control={
                  <Checkbox
                    checked={filters.category === category.id}
                    onChange={() => handleCategoryChange(category.id)}
                  />
                }
                label={`${category.name} (${category.audio_book_count || 0})`}
              />
            ))}
          </Box>
        </AccordionDetails>
      </FilterAccordion>

      <FilterAccordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{t("audioBooks.filter.priceRange")}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ px: 1 }}>
            <PriceSlider
              value={priceRange}
              onChange={handlePriceChange}
              onChangeCommitted={handlePriceCommitted}
              valueLabelDisplay="auto"
              min={0}
              max={200}
              marks={[
                { value: 0, label: "$0" },
                { value: 200, label: "$200" },
              ]}
            />
            <PriceInputContainer>
              <PriceInput
                size="small"
                value={priceRange[0]}
                onChange={handlePriceInputChange("min")}
                onBlur={handlePriceCommitted}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  type: "number",
                }}
                placeholder={t("audioBooks.filter.min")}
              />
              <PriceInput
                size="small"
                value={priceRange[1]}
                onChange={handlePriceInputChange("max")}
                onBlur={handlePriceCommitted}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                  type: "number",
                }}
                placeholder={t("audioBooks.filter.max")}
              />
            </PriceInputContainer>
          </Box>
        </AccordionDetails>
      </FilterAccordion>
    </>
  );
};

const FilterSection = ({
  filters,
  onFilterChange,
  onClearFilters,
  isMobile,
  onClose,
}) => {
  const { t } = useTranslation();
  const { data: categories } = useCategories();

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={true}
        onClose={onClose}
        onOpen={() => {}}
        disableSwipeToOpen
        sx={{
          "& .MuiDrawer-paper": {
            height: "85vh",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <MobileFilterWrapper>
          <MobileHeader>
            <Typography variant="h6" fontWeight={600}>
              {t("audioBooks.filter.filters")}
            </Typography>
            <Box>
              <Button sx={{ mr: 1 }} onClick={onClearFilters}>
                {t("audioBooks.filter.clearAll")}
              </Button>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </MobileHeader>

          <Box sx={{ flex: 1, overflow: "auto", px: 1, py: 2 }}>
            <FilterContent
              filters={filters}
              onFilterChange={onFilterChange}
              categories={categories}
            />
          </Box>

          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Button
              fullWidth
              variant="contained"
              onClick={onClose}
              sx={{ borderRadius: 2 }}
            >
              {t("audioBooks.filter.showResults")}
            </Button>
          </Box>
        </MobileFilterWrapper>
      </SwipeableDrawer>
    );
  }

  return (
    <Box>
      <FilterHeader>{t("audioBooks.filter.filterResults")}</FilterHeader>
      <FilterContent
        filters={filters}
        onFilterChange={onFilterChange}
        categories={categories}
      />
      <Button
        variant="outlined"
        fullWidth
        sx={{ mt: 2 }}
        onClick={onClearFilters}
      >
        {t("audioBooks.filter.clearAllFilters")}
      </Button>
    </Box>
  );
};

export default FilterSection;
